import { useEffect, useState } from 'react';
import { canAccessAdmin, subscribeIamAccess } from './iamAccess';

/**
 * Whether the signed-in user holds the admin role, as a subscribed hook.
 *
 * Reads the same IAM access profile that AdminRouteGuard gates on, so the
 * chrome and the route can never disagree about who is an admin. Subscribing
 * matters: the profile is fetched after first paint, so a plain one-shot
 * canAccessAdmin() call renders `false` for the first frames and leaves an
 * admin looking at a clinician-scoped view until something else forces a
 * re-render. subscribeIamAccess fires immediately with current state and again
 * on every refresh, so the answer corrects itself.
 *
 * PRESENTATION ONLY. This decides what is worth showing, never what may be
 * reached. The server scopes /v1/jobs by owner and admin role independently
 * (_owner_scope in march.api.job_routes), and /jobs sits behind
 * AdminRouteGuard. A tampered client gets a different-looking drawer, not
 * another patient's data.
 */
export function useIsAdmin(): boolean {
  const [isAdmin, setIsAdmin] = useState(() => canAccessAdmin());

  useEffect(() => subscribeIamAccess(() => setIsAdmin(canAccessAdmin())), []);

  return isAdmin;
}
